import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { toCanvas } from "html-to-image";
import { jsPDF } from "jspdf";
import { Check, FileDown, Pencil, Plus, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/features/auth/hooks/useAuthentication";
import { useChatContext } from "@/features/chat/context/ChatContext";
import {
  DashboardAlertBanner,
  DashboardFreshnessNote,
  DashboardGrid,
  dashboardSearchSchema,
  FilterSheet,
  OwnerNotesPanel,
  useDashboardBySlugQuery,
} from "@/features/dashboard";
import { widgetKeys } from "@/features/dashboard/api/keys";
import { ApiError } from "@/shared/lib/errors";
import { usePageHeader } from "@/shared/store/page-header";
import { Button } from "@/shared/ui";

// How long an export waits for widgets to finish loading, and for chart animations to settle.
const EXPORT_LOAD_TIMEOUT_MS = 8000;
const EXPORT_ANIMATION_SETTLE_MS = 1500;

export const Route = createFileRoute("/_authenticated/dashboard/$slug")({
  validateSearch: dashboardSearchSchema,
  component: DynamicDashboardPage,
});

function DynamicDashboardPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { data: dashboard, isLoading, error } = useDashboardBySlugQuery(slug);
  const { setTitle, reset } = usePageHeader();
  const [isEditMode, setIsEditMode] = useState(false);
  const queryClient = useQueryClient();
  const { openChatWithDraft } = useChatContext();
  const { organization } = useAuth();

  // Heading for the exported PDF, e.g. "JMF Weekly Cashflow".
  const exportHeading = [organization?.name, dashboard?.title].filter(Boolean).join(" ");

  // Export to PDF. Rather than window.print() (which flattens the react-grid
  // layout into a single phone-width column), we snapshot the rendered dashboard
  // DOM to a canvas — preserving the exact on-screen multi-column layout and
  // charts — then slice that image across A4 pages into a downloadable PDF.
  const exportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPdf = async () => {
    const node = exportRef.current;
    if (!node || isExporting) return;

    // Force the client-facing PDF to render in LIGHT theme regardless of the
    // viewer's current mode: strip the `dark` class off <html> so every
    // CSS-variable-driven color (card backgrounds, text, borders, tables, the
    // export heading) flips to white/dark-ink. Restored in `finally`.
    const rootEl = document.documentElement;
    const wasDark = rootEl.classList.contains("dark");

    // Reveal the print-only heading + light theme, then wait two frames so the
    // new styles paint before we snapshot.
    setIsExporting(true);
    if (wasDark) rootEl.classList.remove("dark");
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Widgets still loading render as pulsing placeholders, and chart lines animate
    // in over ~1s: snapshotting at once captures empty tables and half-drawn lines.
    // Wait (bounded) for the placeholders to go, then for the animations to finish.
    const waitDeadline = Date.now() + EXPORT_LOAD_TIMEOUT_MS;
    while (node.querySelector(".animate-pulse") && Date.now() < waitDeadline) {
      await new Promise((r) => setTimeout(r, 150));
    }
    await new Promise((r) => setTimeout(r, EXPORT_ANIMATION_SETTLE_MS));

    // Tables scroll sideways on screen, but a snapshot only sees the visible part, so
    // wide tables lose their right-hand columns. Shrink each overflowing table just
    // enough to fit its card for the capture, then put it back.
    const shrunk: HTMLElement[] = [];
    node.querySelectorAll<HTMLElement>("*").forEach((el) => {
      const overflowX = getComputedStyle(el).overflowX;
      const table = el.firstElementChild;
      if ((overflowX !== "auto" && overflowX !== "scroll") || table?.tagName !== "TABLE") return;
      if (el.scrollWidth <= el.clientWidth + 1) return;
      (table as HTMLElement).style.zoom = String(el.clientWidth / el.scrollWidth);
      shrunk.push(table as HTMLElement);
    });

    try {
      const canvas = await toCanvas(node, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        // Skip the per-widget "⋯" action menus in the export.
        filter: (el) => !(el instanceof HTMLElement && el.dataset.exportHide === "true"),
      });

      // Fit the capture to A4 landscape width and paginate down its height.
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const margin = 24;
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const contentW = pageW - margin * 2;
      const contentH = pageH - margin * 2;

      // How many source pixels map to one PDF page of content height.
      const pxPerPage = (canvas.width * contentH) / contentW;

      // Card boundaries (in canvas px): each widget card is a region we must not
      // slice through. We map every grid item's on-screen rect into canvas space
      // and treat its interior as "forbidden" for a page break.
      const nodeRect = node.getBoundingClientRect();
      const scale = canvas.width / nodeRect.width;
      const cards = Array.from(node.querySelectorAll<HTMLElement>(".react-grid-item"));
      const forbidden = cards
        .map((el) => {
          const r = el.getBoundingClientRect();
          return {
            top: (r.top - nodeRect.top) * scale,
            bottom: (r.bottom - nodeRect.top) * scale,
          };
        })
        .sort((a, b) => a.top - b.top);

      // Largest break Y in (start, limit] that doesn't cut through a card. If the
      // card that `limit` lands inside starts at/before `start` (taller than a
      // page), no clean break exists — signal a hard cut by returning `start`.
      const findBreak = (start: number, limit: number): number => {
        const cut = forbidden.find((iv) => iv.top < limit - 0.5 && limit < iv.bottom - 0.5);
        if (!cut) return limit;
        return cut.top > start + 1 ? cut.top : start;
      };

      const slices: Array<[number, number]> = [];
      let start = 0;
      while (start < canvas.height - 0.5) {
        const limit = start + pxPerPage;
        if (limit >= canvas.height) {
          slices.push([start, canvas.height]);
          break;
        }
        let end = findBreak(start, limit);
        if (end <= start) end = limit; // card taller than a page → hard slice
        slices.push([start, end]);
        start = end;
      }

      slices.forEach(([sliceStart, sliceEnd], page) => {
        if (page > 0) pdf.addPage();
        const sliceH = sliceEnd - sliceStart;

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceH;
        const ctx = slice.getContext("2d");
        if (!ctx) return;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, sliceStart, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        const drawH = (sliceH * contentW) / canvas.width;
        pdf.addImage(
          slice.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          contentW,
          drawH,
          undefined,
          "FAST"
        );
      });

      pdf.save(`${exportHeading} - ${format(new Date(), "yyyy-MM-dd")}.pdf`);
    } finally {
      shrunk.forEach((table) => {
        table.style.zoom = "";
      });
      if (wasDark) rootEl.classList.add("dark");
      setIsExporting(false);
    }
  };

  const handleAddWidget = () => {
    openChatWithDraft(
      "Add a widget to this dashboard showing ",
      dashboard
        ? { dashboardId: dashboard.id, startDate: search.startDate, endDate: search.endDate }
        : undefined
    );
  };

  // Manual re-fetch. Re-runs each widget's query against whatever its data
  // source currently serves — it does NOT sync new data from an upstream ERP;
  // that's a separate action on Integrations.
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: widgetKeys.queries() });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Check if dashboard has date-range filter
  const hasDateRangeFilter = dashboard?.filterConfig?.some((f) => f.type === "date-range") ?? false;

  // Check if filters are ready (dates are set if date-range filter exists)
  const filtersReady = !hasDateRangeFilter || (!!search.startDate && !!search.endDate);

  // Set default date range (last 6 months) if no dates are set
  useEffect(() => {
    if (dashboard && hasDateRangeFilter && !search.startDate && !search.endDate) {
      const today = new Date();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);

      void navigate({
        to: ".",
        search: {
          startDate: sixMonthsAgo.toISOString().split("T")[0],
          endDate: today.toISOString().split("T")[0],
        },
        replace: true,
      });
    }
  }, [dashboard, hasDateRangeFilter, search.startDate, search.endDate, navigate]);

  // Set the page title when dashboard loads
  useEffect(() => {
    if (dashboard) {
      setTitle(dashboard.title);
    }

    // Reset on unmount
    return () => {
      reset();
    };
  }, [dashboard, setTitle, reset]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Gated on `!dashboard` alone (not `error || !dashboard`): a background
  // refetch that fails (e.g. React Query's refetchOnWindowFocus, or a
  // raced/stale native auth token — see the token-refresh work on
  // feat/native-mobile-home) sets `error` without clearing already-loaded
  // `dashboard` data. Tearing down a working view to tell the user to log
  // out on a transient failure is worse than the stale-data error it hides.
  if (!dashboard) {
    // A 401 here means the session/token was rejected, not that the
    // dashboard doesn't exist — rendering the same "not found" copy for
    // both hid a real auth bug behind what looked like a data problem.
    const isAuthError = error instanceof ApiError && error.status === 401;
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-foreground">
            {isAuthError ? "Session expired" : "Dashboard not found"}
          </h2>
          <p className="mt-2 text-muted-foreground">
            {isAuthError
              ? "Your session needs to be refreshed. Please log out and back in."
              : "The dashboard you're looking for doesn't exist or you don't have access to it."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-theme min-h-full bg-background text-foreground p-6 space-y-6">
      {/* Header controls: Date Range, Edit, and Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:justify-end md:items-center md:gap-3">
        {/* Manual re-fetch of the widget queries. Freshness note (real sync info,
            "TCS iON Live" only — see DashboardFreshnessNote) sits to its left,
            same slot the old "Data as of" text occupied. */}
        <div className="flex flex-row items-center gap-3 md:mr-auto">
          <DashboardFreshnessNote enabled={slug === "tcsion-live"} />
          <Button
            variant="outline"
            size="sm"
            className="gap-2 min-h-[44px] md:min-h-0"
            onClick={() => void handleRefresh()}
            disabled={isRefreshing}
            aria-label="Reload dashboard view"
            title="Re-fetches the latest available data. New data from a connected system (e.g. TCS iON) needs a sync from Integrations."
          >
            <RefreshCw className={isRefreshing ? "size-4 animate-spin" : "size-4"} />
            <span className="hidden md:inline">Reload View</span>
          </Button>
        </div>
        {/* Mobile: Edit and Filters on second row, side-by-side */}
        {/* Desktop: Edit and Filters in control group */}
        <div className="flex flex-row items-center gap-2 md:gap-3">
          {/* Export to PDF: snapshots the dashboard and downloads a PDF. */}
          <Button
            variant="outline"
            size="sm"
            className="gap-2 min-h-[44px] md:min-h-0"
            onClick={() => void handleExportPdf()}
            disabled={isExporting}
            aria-label="Export dashboard as PDF"
          >
            <FileDown className={isExporting ? "size-4 animate-pulse" : "size-4"} />
            <span className="hidden md:inline">{isExporting ? "Exporting…" : "Export PDF"}</span>
          </Button>
          {/* Add widget: opens the AI chat pre-scoped to this dashboard */}
          <Button
            variant="outline"
            size="sm"
            className="gap-2 min-h-[44px] md:min-h-0"
            onClick={handleAddWidget}
          >
            <Plus className="size-4" />
            <span className="hidden md:inline">Add widget</span>
          </Button>
          {/* Edit button */}
          <Button
            variant={isEditMode ? "default" : "outline"}
            size="sm"
            className="gap-2 min-h-[44px] md:min-h-0"
            onClick={() => setIsEditMode(!isEditMode)}
          >
            {isEditMode ? <Check className="size-4" /> : <Pencil className="size-4" />}
            <span className="hidden md:inline">{isEditMode ? "Done" : "Edit"}</span>
          </Button>
          {/* Filters button */}
          <FilterSheet dashboard={dashboard} />
        </div>
      </div>

      {/* Exportable region: this subtree is what gets snapshotted into the PDF,
          so it excludes the app chrome + on-screen controls above. */}
      <div ref={exportRef} className="space-y-6 bg-background">
        {/* PDF-only heading: rendered while exporting so it's captured, then removed.
            Supplies the "<Org> <Dashboard>" title + generation date on the PDF. */}
        {isExporting && (
          <div className="pb-2">
            <h1 className="text-2xl font-bold text-foreground">{exportHeading}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Generated {format(new Date(), "MMMM d, yyyy")}
            </p>
          </div>
        )}

        {/* Operational disclaimer on selected JMF dashboards. Copy is hard-coded in
            DashboardAlertBanner, keyed by slug (so weekly-cashflow and the API demo
            get different text). The `weekly-cashflow` slug is shared across orgs, so
            this is also gated on the org being JMF. */}
        <DashboardAlertBanner slug={slug} enabled={/^jmf$/i.test(organization?.name ?? "")} />

        {/* "Executive Summary - Notes for Owner": collapsible free-text updates copied
            live from the workbook's "Notes for Owner" tab. Weekly Cashflow only — the
            API demo dashboard shows the hard-coded disclaimer above instead. */}
        <OwnerNotesPanel enabled={slug === "weekly-cashflow"} />

        {/* Dashboard content - only render when filters are ready */}
        {filtersReady ? (
          <DashboardGrid
            dashboardId={dashboard.id}
            filterConfig={dashboard.filterConfig}
            isEditMode={isEditMode}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
